import { Group } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { useWorkOrderTrackingCalendarComplement } from '@/core/domain/workorder/Tracking/Tracking.hook';
import { WorkOrderTrackingCalendar } from '@/core/domain/workorder/Tracking/Tracking.types';
import { useWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { ComplementOriginTranslator } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { workorderComplementColor } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWorkOrderObservations } from '@/core/domain/workorder/WorkOrdersObservations/WorkOrdersObservations.hooks';
import { useWindowSize } from '@/hooks';
import { applyMask, removeMask } from '@/utils/mask';
import { cellphoneMask } from '@/utils/mask/commonMasks';
import phoneMask from '@/utils/mask/commonMasks/phoneMask';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Modal,
  ModalProps,
  Row,
  Skeleton,
  Statistic,
  Typography,
} from 'antd';
import moment from 'moment';
import { ReactNode } from 'react';
import { FaGlobe, FaMobile, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';
import { useIntl } from 'react-intl';

interface Props extends ModalProps {
  onCancel: () => void;
  workorder?: WorkOrderTrackingCalendar;
}

const iconContact: Record<string, ReactNode> = {
  [ContactType.Mobile]: <FaMobile size={12} />,
  [ContactType.Phone]: <FaPhoneAlt size={12} />,
  [ContactType.WhatsApp]: <FaWhatsapp size={12} />,
  [ContactType.Foreign]: <FaGlobe size={12} />,
};

const WorkorderCalendarModal: React.FC<Props> = ({ workorder, ...props }) => {
  const intl = useIntl();
  const { isMobile } = useWindowSize();

  const complement = useWorkOrderTrackingCalendarComplement(
    workorder?.complementId,
  );

  const complementData = complement.data;

  const workOrderDetail = useWorkOrder(workorder?.workOrderId);
  const customerContact = useCustomerContacts(
    workOrderDetail.data?.customer?.id,
    !!workorder?.workOrderId,
  );

  const isContactLoading =
    workOrderDetail.isLoading || customerContact.isLoading;

  const workOrderObservations = useWorkOrderObservations(
    workorder?.complementId,
  );

  const lastObservations = workOrderObservations.data?.slice(0, 5) || [];

  const phoneNumberContacts =
    customerContact.data?.flatMap((contact) =>
      contact.detailList.filter(
        (detail) =>
          detail.contactType === ContactType.Mobile ||
          detail.contactType === ContactType.Phone ||
          detail.contactType === ContactType.WhatsApp ||
          detail.contactType === ContactType.Foreign,
      ),
    ) || [];

  const isPhoneMask = (value: string) =>
    String(removeMask(value)).length === 10;

  return (
    <Modal
      {...props}
      title="Pré-Visualização do orçamento"
      destroyOnClose
      width={600}
      footer={
        <Group position="center">
          <Button
            type="primary"
            onClick={() =>
              window.open(
                `/app/movements/workOrders/${workorder?.workOrderId}/complements/${workorder?.complementId}/generalInfo`,
                '_blank',
              )
            }
          >
            Visualizar detalhes da OS
          </Button>
        </Group>
      }
    >
      <Group style={{ marginBottom: 16 }} position="between">
        {!!workorder && (
          <StatusTag
            key={workorder?.workOrderId}
            label={intl.formatMessage({
              id: `workOrders.${workorder?.status}`,
            })}
            color={workorderComplementColor[workorder?.status]}
            size="lg"
          />
        )}
        <big>
          <strong>{workorder?.complementIdentifier}</strong>
        </big>
      </Group>

      <Skeleton loading={complement.isLoading}>
        <Typography.Title level={5}>Total do orçamento</Typography.Title>

        <Row gutter={[24, 24]}>
          <Col>
            <Statistic
              title="Total"
              value={complementData?.total}
              formatter={(value) => formatterPrice(Number(value))}
            />
          </Col>

          <Col>
            <Divider type="vertical" style={{ minHeight: '100%' }} />
          </Col>

          <Col>
            <Statistic
              title="Total de peças"
              value={complementData?.partsTotal}
              formatter={(value) => formatterPrice(Number(value))}
            />
          </Col>

          <Col>
            <Divider type="vertical" style={{ minHeight: '100%' }} />
          </Col>

          <Col>
            <Statistic
              title="Total de serviços"
              value={complementData?.servicesTotal}
              formatter={(value) => formatterPrice(Number(value))}
            />
          </Col>
        </Row>

        <Divider />

        <Descriptions
          layout="horizontal"
          column={1}
          labelStyle={{ fontWeight: 'bold' }}
          size="middle"
          bordered
        >
          <Descriptions.Item label="Empresa" style={{ padding: 6 }}>
            {complementData?.companyName}
          </Descriptions.Item>

          <Descriptions.Item label="Cliente" style={{ padding: 6 }}>
            {complementData?.clientName}
          </Descriptions.Item>

          <Descriptions.Item
            label="Telefones do cliente"
            style={{ padding: 6 }}
          >
            <Skeleton loading={isContactLoading} paragraph={false}>
              {!!phoneNumberContacts.length ? (
                <>
                  {phoneNumberContacts.map((phone) => {
                    return (
                      <Group gap={6} key={phone.id}>
                        {iconContact[phone.contactType]}
                        {phone.contactType === ContactType.Foreign
                          ? phone.value
                          : applyMask(
                              removeMask(phone.value),
                              isPhoneMask(phone.value)
                                ? phoneMask
                                : cellphoneMask,
                            )}
                      </Group>
                    );
                  })}
                </>
              ) : (
                <span style={{ color: 'darkgray' }}>Sem valores</span>
              )}
            </Skeleton>
          </Descriptions.Item>

          <Descriptions.Item label="Veículo" style={{ padding: 6 }}>
            {complementData?.vehicleName || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Placa" style={{ padding: 6 }}>
            {complementData?.licensePlate || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Origem" style={{ padding: 6 }}>
            {complementData?.complementOrigin
              ? ComplementOriginTranslator[complementData?.complementOrigin]
              : '-'}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <Descriptions
          layout="vertical"
          column={isMobile ? 1 : 2}
          labelStyle={{ fontWeight: 'bold', margin: 6 }}
          contentStyle={{ margin: 6 }}
          size="middle"
          bordered
        >
          <Descriptions.Item label="Data de entrada" style={{ padding: 6 }}>
            {complementData?.entryDate
              ? moment(complementData.entryDate).format('LLL')
              : ''}
          </Descriptions.Item>

          <Descriptions.Item label="Data de autorização" style={{ padding: 6 }}>
            {complementData?.authorizationDate
              ? moment(complementData.authorizationDate).format('LLL')
              : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Saída prevista" style={{ padding: 6 }}>
            {complementData?.estimatedLeaveDate
              ? moment(complementData.estimatedLeaveDate).format('LLL')
              : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Data de fechamento" style={{ padding: 6 }}>
            {complementData?.closingDate
              ? moment(complementData.closingDate).format('LLL')
              : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Data de entrega" style={{ padding: 6 }}>
            {complementData?.returnDate
              ? moment(complementData.returnDate).format('LLL')
              : '-'}
          </Descriptions.Item>
        </Descriptions>

        <Skeleton loading={workOrderObservations.isLoading}>
          <Divider />

          <Group
            gap={10}
            position="between"
            align="center"
            style={{ marginBottom: 10 }}
          >
            <h2 style={{ lineHeight: 1 }}>Últimas 5 observações</h2>

            <a
              href={`/app/movements/workOrders/${workorder?.workOrderId}/complements/${workorder?.complementId}/observations`}
            >
              Ver todos
            </a>
          </Group>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {!lastObservations.length && (
              <Empty description="Não há observações para esse complemento" />
            )}

            {lastObservations.map((observation) => (
              <Card
                key={observation.id}
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                bodyStyle={{ padding: 10 }}
              >
                <Group gap={10}>
                  <span>{observation.employeeName}</span>{' '}
                  <span style={{ color: 'darkgray' }}>
                    {moment(observation.createdAt).format('DD/MM/YYY HH:mm')}
                  </span>
                </Group>

                <p style={{ margin: 0, fontSize: '1.15rem' }}>
                  {observation.observation}
                </p>
              </Card>
            ))}
          </div>
        </Skeleton>
      </Skeleton>
    </Modal>
  );
};

export default WorkorderCalendarModal;
